import {
  GoogleGenAI,
  LiveCallbacks,
  LiveServerMessage,
  Modality,
  Session,
} from "@google/genai/web"

import airbrake from "src/airbrake-notifier"
import axios from "src/axios"

import { PcmPlayer } from "./pcmPlayer"

// Call close() to tear down permanently (e.g. on unmount).
export class GeminiLiveSession {
  private connection: Session | null = null
  private player: PcmPlayer | null = null
  private destroyed = false
  private currentSessionState = { intentionallyClosed: false }
  // Updated via sessionResumptionUpdate messages; passed on reconnect to
  // preserve session context across WebSocket connection resets (~10 min limit).
  private resumptionHandle: string | null = null

  private constructor(
    private readonly chimeMeetingId: string,
    private readonly targetLanguageCode: string,
    private readonly onFatalClose: VoidFunction
  ) {}

  static async createAndConnect({
    chimeMeetingId,
    targetLanguageCode,
    onFatalClose,
  }: {
    chimeMeetingId: string
    targetLanguageCode: string
    onFatalClose: VoidFunction
  }): Promise<GeminiLiveSession> {
    const instance = new GeminiLiveSession(
      chimeMeetingId,
      targetLanguageCode,
      onFatalClose
    )
    try {
      await instance.setupConnection()
    } catch (error) {
      instance.close()
      throw error
    }
    return instance
  }

  send(pcm: Uint8Array, sampleRate: number): void {
    if (this.destroyed || !this.connection) return
    try {
      this.connection.sendRealtimeInput({
        audio: {
          data: btoa(String.fromCharCode(...pcm)),
          mimeType: `audio/pcm;rate=${sampleRate}`,
        },
      })
    } catch (error) {
      airbrake.notify({
        message: "Gemini Live connection lost while sending audio",
        params: { name: "GeminiLiveSession" },
        error,
      })
      if (!this.destroyed) {
        this.onFatalClose()
      }
    }
  }

  close(): void {
    this.destroyed = true
    this.currentSessionState.intentionallyClosed = true
    this.connection?.close()
    this.connection = null
    this.player?.close()
    this.player = null
  }

  private async fetchGeminiLiveToken(): Promise<string | null> {
    try {
      const res = await axios.post("/api/gemini_live_token", {
        chime_id: this.chimeMeetingId,
      })
      return res.data?.token
    } catch (e) {
      airbrake.notify({
        message: "Error from GeminiLiveTokensController",
        params: { name: "GeminiLiveSession" },
        error: e,
      })
      return null
    }
  }

  private async setupConnection(): Promise<void> {
    const token = await this.fetchGeminiLiveToken()
    if (!token) throw new Error("Failed to fetch Gemini Live token")
    if (this.destroyed) return

    const player = await PcmPlayer.create()
    if (this.destroyed) {
      player.close()
      return
    }
    this.player?.close()
    this.player = player

    // Each session gets its own state object so stale onclose callbacks from
    // a reconnecting session don't fire onFatalClose against the new session.
    const sessionState = { intentionallyClosed: false }
    this.currentSessionState = sessionState

    const config = {
      responseModalities: [Modality.AUDIO],
      translationConfig: {
        targetLanguageCode: this.targetLanguageCode,
        echoTargetLanguage: true,
      },
      contextWindowCompression: { slidingWindow: {} }, // Compresses older context instead of terminating at the 15-min audio limit.
      sessionResumption: { handle: this.resumptionHandle ?? undefined }, // Enables server-driven resumption tokens so reconnects preserve session state.
    }

    const callbacks: LiveCallbacks = {
      onmessage: (message: LiveServerMessage) => {
        if (message.data) this.player?.enqueueAudio(message.data)

        // Keep the latest resumption handle so the next connection can resume after the WebSocket resets (~10 min connection limit).
        const update = message.sessionResumptionUpdate
        if (update?.resumable && update.newHandle) {
          this.resumptionHandle = update.newHandle
        }
      },
      onerror: (errorEvent: ErrorEvent) => {
        airbrake.notify({
          error: errorEvent,
          message: `Gemini Live error: ${errorEvent.message}`,
          params: { name: "GeminiLiveSession" },
        })
        if (!sessionState.intentionallyClosed && !this.destroyed) {
          this.onFatalClose()
        }
      },
      onclose: (event: CloseEvent) => {
        if (sessionState.intentionallyClosed || this.destroyed) return
        // The server periodically resets the WebSocket connection (~10 min).
        // Reconnect using the stored resumption handle to continue the session.
        if (this.resumptionHandle) {
          this.reconnect()
          return
        }
        // No resumption handle — unexpected failure (bad config, auth, etc.)
        airbrake.notify({
          message: "Gemini Live session closed unexpectedly",
          params: { name: "GeminiLiveSession", closeEvent: event },
        })
        this.onFatalClose()
      },
    }

    const ai = new GoogleGenAI({
      apiKey: token,
      httpOptions: { apiVersion: "v1alpha" },
    })

    const connection = await ai.live.connect({
      model: process.env.GEMINI_LIVE_API_MODEL,
      config,
      callbacks,
    })
    if (this.destroyed) {
      connection.close()
      return
    }
    this.connection = connection
  }

  private async reconnect(): Promise<void> {
    if (this.destroyed) return
    this.currentSessionState.intentionallyClosed = true
    this.connection?.close()
    this.connection = null
    try {
      await this.setupConnection()
    } catch (error) {
      if (!this.destroyed) {
        airbrake.notify({
          error,
          message: "GeminiLiveSession failed to reconnect",
          params: { name: "GeminiLiveSession" },
        })
        this.onFatalClose()
      }
    }
  }
}
